import type { DomTour, PassoTour } from '../componentes/tour-passos';

/**
 * U15 F2: dicas na primeira visita a cada tela que tem ajuda. Reaproveitam o tour de holofote (`componentes/tour.tsx`):
 * 2 a 3 passos curtos ("aqui você cria", "aqui você filtra", "aqui fica a ajuda") sobre alvos `data-tour` que já existem
 * na tela. A "dica vista" fica no usuário (`usuarios.dicas_vistas`, vale entre aparelhos). As regras ficam aqui, puras.
 */

/** Telas com aula de ajuda (as do curso "Ajuda das telas"): só nelas as dicas aparecem. */
export const TELAS_COM_DICA: readonly string[] = [
  'inicio',
  'conversas/whatsapp',
  'conversas/email',
  'conversas/filas',
  'financeiro/entradas-saidas',
  'despesas/despesas',
  'contas/a-receber',
  'contas/a-pagar',
  'contas/cobrancas',
  'pipeline/pipeline',
  'portal/acessos',
];

/**
 * Quando a dica da tela pode aparecer: a tela tem dica, a pessoa ainda não a viu (no servidor nem neste aparelho agora) e o
 * tour do primeiro login estava concluído quando a tela abriu (não encadeia as dicas logo depois do tour).
 */
export function dicaPendente(o: { tela: string; onboardingConcluidoAoAbrir: boolean; vistas: readonly string[] | undefined; vistasAgora?: ReadonlySet<string> }): boolean {
  return o.onboardingConcluidoAoAbrir && TELAS_COM_DICA.includes(o.tela) && !(o.vistas ?? []).includes(o.tela) && !o.vistasAgora?.has(o.tela);
}

/** Alvos `data-tour` das dicas: o botão primário, o filtro ou a busca da tela e o botão Ajuda. */
export const ALVO_DICA = { cria: 'acao-primaria', filtra: 'filtro', ajuda: 'ajuda-tela' } as const;

/**
 * Os passos da dica desta tela: só os de alvo VISÍVEL agora (tela sem botão primário ou sem filtro não mostra o passo; o
 * da ajuda vale sempre que o botão existe). O texto de cada passo é o da tela quando existe (`ajudaTela.dicasTela.<tela>`),
 * senão o genérico (`ajudaTela.dicas`).
 */
export function passosDaDica(tela: string, dom: Pick<DomTour, 'visivel'>, existe: (chave: string) => boolean): PassoTour[] {
  const passo = (id: 'cria' | 'filtra' | 'ajuda'): PassoTour => {
    const propria = existe(`ajudaTela.dicasTela.${tela}.${id}`);
    return {
      chave: `dica-${id}`,
      alvos: [ALVO_DICA[id]],
      titulo: `ajudaTela.dicas.${id}Titulo`,
      texto: propria ? `ajudaTela.dicasTela.${tela}.${id}` : `ajudaTela.dicas.${id}Texto`,
    };
  };
  return (['cria', 'filtra', 'ajuda'] as const).map(passo).filter((p) => p.alvos.some((a) => dom.visivel(a)));
}
